import { Box, TextareaAutosize, Typography } from "@mui/material"
import type { FC, ReactNode } from "react"
import React, { useRef, useState } from "react"

import { Button } from "@/components/common/Button"
import { TEXT_AREA_MAX_CHARACTERS } from "@/constants/forms"
import useClickOutside from "@/hooks/useClickOutside"
import useKeyPressed from "@/hooks/useKeyPressed"

const CommentForm: FC<{
  value: string
  commentId?: string
  onSubmitComment: (value: string, commentId?: string) => void
  onCancelComment: () => void
}> = ({ value, commentId, onSubmitComment, onCancelComment }): ReactNode => {
  const [updatedValue, setUpdatedValue] = useState<string>(value)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const onChangeValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value
    if (value.length > TEXT_AREA_MAX_CHARACTERS) {
      value = value.slice(0, TEXT_AREA_MAX_CHARACTERS)
    }
    setUpdatedValue(value)
  }

  useClickOutside(onCancelComment, containerRef)
  useKeyPressed("Escape", onCancelComment, containerRef)

  return (
    <div ref={containerRef} className='relative w-full py-3' data-testid='comment-form'>
      <Typography variant='body2' className='pb-2 text-right text-xs font-thin text-blue-gray-400'>
        <span className='text-blue-gray-800' data-testid='comment-form-char-count'>
          {updatedValue?.length ?? 0}
        </span>{" "}
        out of Max length <span data-testid='comment-form-max-chars'>{TEXT_AREA_MAX_CHARACTERS}</span> characters
      </Typography>
      <TextareaAutosize
        minRows={2}
        value={updatedValue}
        className='w-full rounded-md border border-solid border-gray-300 py-3 pl-3 pr-[170px] text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BFDBFE]'
        onChange={onChangeValue}
        placeholder='Add a comment...'
        data-testid='comment-form-textarea'
      />
      <Box className='absolute bottom-7 right-4 space-x-2'>
        <Button
          onClick={() => onCancelComment()}
          size='small'
          appearance='outlined'
          data-testid='comment-form-cancel-button'
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSubmitComment(updatedValue, commentId)}
          disabled={updatedValue === value}
          size='small'
          data-testid='comment-form-submit-button'
        >
          {!value ? "Add" : "Update"}
        </Button>
      </Box>
    </div>
  )
}
export default CommentForm
